import {useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {t} from 'i18next';
import {
  IAIInvestigateConversationDocumentDetailsPayload,
  IAIInvestigateConversationDocumentDetailsResponse,
  IAIInvestigateIndicesListResponse,
  IAIInvestigateIndicesPayload,
  IAIInvestigateOwnersListResponse,
  IAIInvestigateTranscriptDetailsResponse,
  IAIInvestigateTranscriptListResponse,
  IAIInvestigateTranscriptPayload,
  ICreateConversationPayload,
  ICreateConversationResponse,
  ICreateIndexPayload,
  ICreateIndexResponse,
  ICreateTranscriptPayload,
  IEditIndexPayload,
  ISelectedTranscript,
  IToggleIndexResponse,
  ToggleIndexParams,
  IAIInvestigateIndicesDetailsResponse,
  ISavedSearch,
  IUpdateTranscriptPayload,
  IUpdateTranscriptResponse,
  IIndexSourceDocumentCountPayload,
  IIndexSourceDocumentCountResponse,
  IAIInvestigateDocumentSummaryResponse,
  IInvestigateNavigationMasterListPayload,
  IInvestigateNavigationMasterListResponse,
  IRetryInvestigateIndexResponse,
  RetryInvestigateIndexParams,
} from '../../../api/types/aiInvestigate.type';
import {formatToCustomUTC} from '../../../utils/groupTranscriptsByDate';
import {
  createInvestigateIndex,
  createInvestigateTranscript,
  getAIInvestigateIndicesList,
  getAIInvestigateOwnersList,
  getAIInvestigateTranscriptList,
  getAIInvestigateTranscriptDetails,
  deleteInvestigateTranscript,
  deleteInvestigateConversation,
  createInvestigateConversation,
  toggleIndex,
  getAIInvestigateIndicesDetails,
  getAIInvestigateConversationDocumentDetails,
  getSavedSearchList,
  updateInvestigateTranscript,
  editInvestigateIndex,
  getAIInvestigateIndexSourceDocumentCount,
  getAIInvestigateDocumentSummary,
  createInvestigateNavigationMasterList,
  retryInvestigateIndex,
  downloadTranscriptConversation,
  getAIInvestigateIndicesMasterDocListId,
  getAIInvestigateIndicesDetailMasterDocListId,
} from '../../../api/endpoints/aiInvestigateApi';
import {TranscriptDetails} from '../../../components/ai/investigate/chat-interface/InvestigateChatPanel';
import {
  isDuplicateTranscriptNameError,
  TranscriptFormValues,
  TranscriptItemDataType,
} from '../../../components/ai/investigate/AiInvestigateUtils';

export const useGetTranscriptList = (
  payload: IAIInvestigateTranscriptPayload,
  options?: any
) => {
  return useQuery<IAIInvestigateTranscriptListResponse, Error>({
    queryKey: ['getAIInvestigateTranscriptList', payload],
    queryFn: () => getAIInvestigateTranscriptList(payload),
    ...options,
  });
};

export const useGetIndicesList = (
  payload: IAIInvestigateIndicesPayload,
  options?: {enabled?: boolean}
) => {
  return useQuery<IAIInvestigateIndicesListResponse, Error>({
    queryKey: ['getAIInvestigateIndicesList', payload],
    queryFn: () => getAIInvestigateIndicesList(payload),
    enabled: options?.enabled ?? true,
  });
};

export const useInvestigate = () => {
  const queryClient = useQueryClient();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const [selectedTranscriptDetails, setSelectedTranscriptDetails] =
    useState<TranscriptDetails>({
      transcriptId: 0,
      isOwner: true,
      ownerName: '',
      transcriptName: '',
      indexName: '',
      indexId: 0,
      isNew: false,
      documentCount: 0,
    });

  const [disableNewChat, setDisableNewChat] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    error?: string;
    success?: string;
  }>();

  const [indices, setIndices] =
    useState<IAIInvestigateIndicesListResponse | null>(null);

  const handleTranscriptSelect = (selectedTranscript: ISelectedTranscript) => {
    setSelectedTranscriptDetails((prev) => ({
      ...prev,
      transcriptId: selectedTranscript.transcriptId,
      isOwner: selectedTranscript.isOwner,
      indexId: selectedTranscript.indexId,
      indexName: selectedTranscript.indexName,
      transcriptName: selectedTranscript.transcriptName,
      ownerName: selectedTranscript.ownerName,
      isNew: selectedTranscript.isNew ?? false,
      documentCount: selectedTranscript.documentCount,
    }));
  };

  const hanleCreateNewTranscript = () => {
    setIsModalOpen(true);
  };

  const handleCreateTranscript = async (data?: TranscriptFormValues) => {
    if (data?.index?.id == null) return;

    const indexId = Number(data.index.id);
    if (!Number.isFinite(indexId)) return;

    const payload: ICreateTranscriptPayload = {
      transcriptName: data.name.trim(),
      description: data.description.trim(),
      indexId,
    };

    try {
      const response = await createInvestigateTranscript(payload);
      const indexLabel = data?.index?.name;

      const newTranscript: TranscriptItemDataType = {
        transcriptId: response.transcriptId,
        transcriptName: response.transcriptName,
        description: response.description,
        createdDate: response.createdDate,
        formattedCreatedDate: formatToCustomUTC(response.createdDate),
        updatedDate: response.updatedDate,
        ownerName: response.ownerName,
        isMyTranscript: true,
        indexName: String(indexLabel),
        isActiveIndex: true,
        indexID: response.indexId,
        indexDocumentCount: response.indexTotalDocumentCount,
        isNew: true,
      };

      setSelectedTranscriptDetails({
        transcriptId: newTranscript.transcriptId,
        indexId: response.indexId,
        indexName: String(indexLabel),
        transcriptName: response.transcriptName,
        ownerName: response.ownerName,
        isOwner: true,
        isNew: true,
        documentCount: response.indexTotalDocumentCount,
      });

      setIsModalOpen(false);
      // Refresh the cached transcript list so the new chat is present when
      // the user navigates back to the page (avoids a stale-cache read).
      queryClient.invalidateQueries({
        queryKey: ['getAIInvestigateTranscriptList'],
      });
      return newTranscript;
    } catch (error) {
      setToastMessage({
        error: isDuplicateTranscriptNameError(error)
          ? t('ai.investigate.transcripts.modal.duplicateChatName')
          : t('ai.investigate.transcripts.modal.errorMessage'),
      });
      setShowToast(true);
      setIsModalOpen(true);
    }
  };

  return {
    isCollapsed,
    setIsCollapsed,
    selectedTranscriptDetails,
    setSelectedTranscriptDetails,
    disableNewChat,
    setDisableNewChat,
    isModalOpen,
    setIsModalOpen,
    showToast,
    setShowToast,
    toastMessage,
    handleTranscriptSelect,
    hanleCreateNewTranscript,
    handleCreateTranscript,
    indices,
  };
};

export const useCreateInvestigateIndex = (
  onSuccess?: (data: ICreateIndexResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ICreateIndexResponse, Error, ICreateIndexPayload>({
    mutationFn: createInvestigateIndex,
    mutationKey: ['createInvestigateIndex'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useToggleIndex = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IToggleIndexResponse, Error, ToggleIndexParams>({
    mutationFn: ({indexId, payload}) => toggleIndex(indexId, payload),
    mutationKey: ['toggleIndex'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRetryInvestigateIndex = () => {
  return useMutation<
    IRetryInvestigateIndexResponse,
    Error,
    RetryInvestigateIndexParams
  >({
    mutationFn: ({indexId, indexDetailsId}) =>
      retryInvestigateIndex(indexId, indexDetailsId),
    mutationKey: ['retryInvestigateIndex'],
  });
};

export const useDeleteInvestigateTranscript = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteInvestigateTranscript(id),
    mutationKey: ['deleteInvestigateTranscript'],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getAIInvestigateTranscriptList'],
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteInvestigateConversation = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    void,
    Error,
    {transcriptId: number | string; conversationId: number | string}
  >({
    mutationFn: ({transcriptId, conversationId}) =>
      deleteInvestigateConversation(transcriptId, conversationId),
    mutationKey: ['deleteInvestigateConversation'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useEditInvestigateTranscript = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IUpdateTranscriptResponse,
    Error,
    {id: number | string; payload: IUpdateTranscriptPayload}
  >({
    mutationFn: ({id, payload}) => updateInvestigateTranscript(id, payload),
    mutationKey: ['updateInvestigateTranscript'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetOwnersList = () => {
  return useQuery<IAIInvestigateOwnersListResponse[], Error>({
    queryKey: ['useGetOwnersList'],
    queryFn: () => getAIInvestigateOwnersList(),
  });
};

export const useGetTranscriptDetails = (
  transcriptId: number | string,
  options?: {enabled?: boolean; queryKey?: string}
) => {
  return useQuery<IAIInvestigateTranscriptDetailsResponse, Error>({
    queryKey: [
      options?.queryKey ?? 'getAIInvestigateTranscriptDetails',
      transcriptId,
    ],
    queryFn: () => getAIInvestigateTranscriptDetails(transcriptId),
    enabled: options?.enabled,
    placeholderData: (prev) => prev,
  });
};

export const useCreateInvestigateConversation = (
  onSuccess?: (response: ICreateConversationResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ICreateConversationResponse,
    Error,
    {transcriptId: number; payload: ICreateConversationPayload}
  >({
    mutationFn: ({transcriptId, payload}) =>
      createInvestigateConversation(transcriptId, payload),
    mutationKey: ['createInvestigateConversation'],
    onSuccess: (response: ICreateConversationResponse) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetIndicesDetails = (
  onSuccess?: (data: IAIInvestigateIndicesDetailsResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAIInvestigateIndicesDetailsResponse, Error, any>({
    mutationFn: ({indexId, payload}) =>
      getAIInvestigateIndicesDetails(indexId, payload),
    mutationKey: ['getAIInvestigateIndicesDetails'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetSavedSearchList = (
  onSuccess?: (response: ISavedSearch[]) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ISavedSearch[], Error, boolean>({
    mutationFn: (isCountIncluded) => getSavedSearchList(isCountIncluded),
    mutationKey: ['getSavedSearchList'],
    onSuccess: (response: ISavedSearch[]) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetConversationDocumentDetails = (
  onSuccess?: (data: IAIInvestigateConversationDocumentDetailsResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IAIInvestigateConversationDocumentDetailsResponse,
    Error,
    {
      transcriptId: number | string;
      conversationId: number | string;
      payload: IAIInvestigateConversationDocumentDetailsPayload;
    }
  >({
    mutationFn: ({transcriptId, conversationId, payload}) =>
      getAIInvestigateConversationDocumentDetails(
        transcriptId,
        conversationId,
        payload
      ),
    mutationKey: ['getAIInvestigateConversationDocumentDetails'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useEditInvestigateIndex = (
  onSuccess?: (data: ICreateIndexResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    ICreateIndexResponse,
    Error,
    {indexId: number | string; payload: IEditIndexPayload}
  >({
    mutationFn: ({indexId, payload}) => editInvestigateIndex(indexId, payload),
    mutationKey: ['editInvestigateIndex'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIInvestigateIndexSourceDocumentCount = (
  onSuccess?: (data: IIndexSourceDocumentCountResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IIndexSourceDocumentCountResponse,
    Error,
    IIndexSourceDocumentCountPayload
  >({
    mutationFn: getAIInvestigateIndexSourceDocumentCount,
    mutationKey: ['getAIInvestigateIndexSourceDocumentCount'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCreateInvestigateNavigationMasterList = (
  onSuccess?: (data: IInvestigateNavigationMasterListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IInvestigateNavigationMasterListResponse,
    Error,
    IInvestigateNavigationMasterListPayload
  >({
    mutationFn: createInvestigateNavigationMasterList,
    mutationKey: ['createInvestigateNavigationMasterList'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDownloadConversation = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, {transcriptId: number}>({
    mutationFn: (params) => downloadTranscriptConversation(params.transcriptId),
    mutationKey: ['downloadTranscriptConversation'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIInvestigateDocumentSummary = (
  onSuccess?: (data: IAIInvestigateDocumentSummaryResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IAIInvestigateDocumentSummaryResponse,
    Error,
    {indexId: number; documentId: number, docId: string}
  >({
    mutationFn: ({indexId, documentId, docId}) =>
      getAIInvestigateDocumentSummary(indexId, documentId, docId),
    mutationKey: ['getAIInvestigateDocumentSummary'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIInvestigateIndicesMasterDocListId = (
  onSuccess?: (data: IInvestigateNavigationMasterListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IInvestigateNavigationMasterListResponse,
    Error,
    {indexId: number | string}
  >({
    mutationFn: ({indexId}) => getAIInvestigateIndicesMasterDocListId(indexId),
    mutationKey: ['getAIInvestigateIndicesMasterDocListId'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIInvestigateIndicesDetailMasterDocListId = (
  onSuccess?: (data: IInvestigateNavigationMasterListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IInvestigateNavigationMasterListResponse,
    Error,
    {indexdetailId: number | string; type: string}
  >({
    mutationFn: ({indexdetailId, type}) =>
      getAIInvestigateIndicesDetailMasterDocListId(indexdetailId, type),
    mutationKey: ['getAIInvestigateIndicesMasterDocListId'],
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
